import * as Dialog from '@radix-ui/react-dialog'
import { useCallback, useState } from 'react'
import { X, Copy, Check, Download } from 'lucide-react'
import { Button } from '@/components/ui/Button'
import { ARTIFACT_BY_TYPE } from '@/lib/artifactCatalog'
import type { Artifact } from '@/types/api'

interface ArtifactPreviewModalProps {
  artifact: Artifact | null
  /** True when the artifact body is still being fetched. */
  loading: boolean
  onClose: () => void
}

/**
 * Markdown preview modal. Renders `artifact.content` as plain text in a
 * scrolling code block — react-markdown can land in a follow-up commit
 * once we settle on a typography scheme. The Copy button writes the raw
 * markdown to the clipboard; Download triggers a browser download
 * straight from a Blob, no backend round-trip.
 */
export function ArtifactPreviewModal({
  artifact,
  loading,
  onClose,
}: ArtifactPreviewModalProps) {
  const [copied, setCopied] = useState(false)
  const open = artifact !== null || loading

  const def = artifact ? ARTIFACT_BY_TYPE[artifact.type] : null
  const title = def?.label ?? artifact?.title ?? 'Artifact'

  const handleCopy = useCallback(() => {
    if (!artifact?.content) return
    void navigator.clipboard
      .writeText(artifact.content)
      .then(() => {
        setCopied(true)
        window.setTimeout(() => setCopied(false), 1500)
      })
      .catch(() => undefined)
  }, [artifact])

  const handleDownload = useCallback(() => {
    if (!artifact?.content) return
    const blob = new Blob([artifact.content], { type: 'text/markdown' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `${artifact.type}.md`
    document.body.appendChild(a)
    a.click()
    a.remove()
    URL.revokeObjectURL(url)
  }, [artifact])

  return (
    <Dialog.Root
      open={open}
      onOpenChange={(next) => {
        if (!next) onClose()
      }}
    >
      <Dialog.Portal>
        <Dialog.Overlay
          data-testid="artifact-preview-overlay"
          className="fixed inset-0 z-40 bg-black/40 backdrop-blur-sm"
        />
        <Dialog.Content
          data-testid="artifact-preview"
          className="fixed left-1/2 top-1/2 z-50 -translate-x-1/2 -translate-y-1/2 w-[min(840px,calc(100vw-2rem))] max-h-[80vh] flex flex-col rounded-lg border border-border-default bg-bg-primary shadow-xl"
        >
          <header className="flex items-center justify-between gap-3 px-5 py-4 border-b border-border-default">
            <Dialog.Title className="text-sm font-display font-semibold text-text-primary">
              {title}
            </Dialog.Title>
            <Dialog.Close asChild>
              <button
                aria-label="Close"
                className="text-text-tertiary hover:text-text-primary transition-colors"
              >
                <X size={16} />
              </button>
            </Dialog.Close>
          </header>

          <div className="flex-1 overflow-auto px-5 py-4">
            {loading || !artifact?.content ? (
              <div className="text-sm text-text-tertiary">
                {loading ? 'Loading preview…' : 'No content yet.'}
              </div>
            ) : (
              <pre
                data-testid="artifact-preview-content"
                className="whitespace-pre-wrap break-words text-sm text-text-primary leading-relaxed font-mono"
              >
                {artifact.content}
              </pre>
            )}
          </div>

          <footer className="flex items-center justify-end gap-2 px-5 py-3 border-t border-border-default">
            <Button
              size="sm"
              variant="secondary"
              onClick={handleCopy}
              disabled={!artifact?.content}
              data-testid="artifact-preview-copy"
            >
              {copied ? <Check size={14} /> : <Copy size={14} />}
              {copied ? 'Copied' : 'Copy markdown'}
            </Button>
            <Button
              size="sm"
              onClick={handleDownload}
              disabled={!artifact?.content}
              data-testid="artifact-preview-download"
            >
              <Download size={14} />
              Download
            </Button>
          </footer>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  )
}
